import React, { useEffect, useState } from 'react';
import { Button, Input, Radio, message } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { pauseManagedBilling } from 'api/insuranceBilling';
import type { ManagedBillingScopeReq, ManagedBillingVo } from 'types/insuranceBilling';
import CardRadioGroup from '../CardRadioGroup';
import ConfirmModal from './ConfirmModal';
import EhrTiles from './EhrTiles';
import ProviderPicker from './ProviderPicker';
import { CLAIM_OFF_REASONS, NOTE_MAX, OTHERS_REASON, composeReason } from './utils';
import s from './s.module.less';

type Step = 'prov' | 'ehr' | 'reason';

const PROVIDER_UNRELIABLE_EHRS = ['SimplePractice', 'Optimantra'];

type Props = {
    open: boolean;
    state: ManagedBillingVo;
    isGroup: boolean;
    selfProviderId?: number;
    onClose: () => void;
    onDone: (next: ManagedBillingVo) => void;
};

// 原型 confirmClaimOff：一步一个问题。group owner：providers → EHRs → 原因；solo：EHRs → 原因。
// 不预选任何项（对话框不该看起来已经替用户做了决定）
export default function TurnOffModal({ open, state, isGroup, selfProviderId, onClose, onDone }: Props) {
    // 名册为空（AskChart 还没有 rendering provider）时没有可选的人，按 solo 走
    const group = isGroup && state.providers.length > 0;
    const steps: Step[] = group ? ['prov', 'ehr', 'reason'] : ['ehr', 'reason'];
    const [at, setAt] = useState(0);
    const [provScope, setProvScope] = useState('');
    const [provIds, setProvIds] = useState<number[]>([]);
    const [ehrScope, setEhrScope] = useState('');
    const [ehrs, setEhrs] = useState<string[]>([]);
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setAt(0);
        setProvScope('');
        setProvIds([]);
        setEhrScope('');
        setEhrs([]);
        setReason('');
        setNote('');
    }, [open]);

    const allEhrNames = state.ehrs.map((e) => e.ehr);
    const billableRows = state.ehrs.filter((e) => e.on);
    // provider 在每个仍在计费的 EHR 里都已关掉 = 对他整个账号已关，Step 1 不能再选
    const offEverywhereIds = group
        ? state.providers
              .filter((p) => billableRows.length > 0 && billableRows.every((e) => e.offForProviderIds.includes(p.id)))
              .map((p) => p.id)
        : [];
    // Step 2 列出全部 EHR，置灰：①已对所有人关掉的；②group 下所选 provider 在其中**全都**已关的
    //（只要还有一个所选的人在计费，关掉就有效果，不挡）
    const targetProvIds = provScope === 'some' ? provIds : state.providers.map((p) => p.id);
    const disabledEhrs = state.ehrs
        .filter(
            (e) =>
                !e.on ||
                (group && targetProvIds.length > 0 && targetProvIds.every((id) => e.offForProviderIds.includes(id))),
        )
        .map((e) => e.ehr);
    const pickedEhrs = allEhrNames.filter((n) => ehrs.includes(n) && !disabledEhrs.includes(n));
    // 还有可关的人 / 可关的 EHR 才能继续：全部已关时选「All」提交也什么都不会改。
    // 一个 EHR 都没连的账号例外：「All」就是关掉整个账号，照样可提交
    const anyProvLeft = state.providers.length > offEverywhereIds.length;
    const anyEhrLeft = !allEhrNames.length || allEhrNames.length > disabledEhrs.length;
    const provValid = (provScope === 'all' && anyProvLeft) || (provScope === 'some' && provIds.length > 0);
    const ehrValid = (ehrScope === 'all' && anyEhrLeft) || (ehrScope === 'some' && pickedEhrs.length > 0);
    // 这两家 EHR 的 visit 不一定带 rendering provider，按 provider 关不住；已停出账的也算（重开后还会出账）
    const mayMissProvider = allEhrNames.some((n) => PROVIDER_UNRELIABLE_EHRS.includes(n));
    const step = steps[at];
    const STEP_VALID: Record<Step, boolean> = { prov: provValid, ehr: ehrValid, reason: !!reason };
    const stepValid = STEP_VALID[step];
    const last = at === steps.length - 1;

    const handleSubmit = async () => {
        if (!reason || !ehrValid || (group && !provValid)) return;
        const someProviders = group && provScope === 'some';
        const req: ManagedBillingScopeReq = {
            scope: ehrScope === 'all' ? 'all' : 'ehrs',
            ehrs: ehrScope === 'all' ? undefined : pickedEhrs,
            providerIds: someProviders ? provIds : undefined,
            reason: composeReason(reason, note),
        };
        setSubmitting(true);
        const res: any = await pauseManagedBilling(req);
        setSubmitting(false);
        // 失败文案由全局 toast 展示，保留弹窗供重试
        if (res?.error) return;
        message.success('Managed billing turned off');
        onDone(res?.data?.data);
    };

    const intro =
        at === 0 ? (
            <>
                <p className={s.p}>
                    Klarity stops billing insurance for you. No new visits are pulled in, no claims are created or
                    submitted, and new denials are not worked. Anything not billed while this is off stays unbilled.
                </p>
                <div className={s.callout}>
                    <InfoCircleFilled />
                    <span>
                        Claims already with a payer keep going, and we work them through to payment.{' '}
                        <b>Our usual fee applies when they are paid.</b> Prepared claims that were not sent are
                        cancelled but stay on your dashboard. You can turn this back on any time, and we pick up from
                        that day.
                    </span>
                </div>
            </>
        ) : null;

    let body: React.ReactNode = null;
    if (step === 'prov') {
        body = (
            <>
                {intro}
                <div className={s.label}>Stop billing for</div>
                <div className={s.scope}>
                    <CardRadioGroup
                        value={provScope}
                        onChange={setProvScope}
                        options={[
                            {
                                value: 'all',
                                title: 'All providers',
                                desc: 'Klarity stops billing for everyone in the practice.',
                            },
                            {
                                value: 'some',
                                title: 'Selected providers only',
                                desc: 'Klarity keeps billing the providers you do not select.',
                            },
                        ]}
                    />
                </div>
                {provScope === 'some' && (
                    <div className={s.pickWrap}>
                        {mayMissProvider && (
                            <div className={s.callout}>
                                <InfoCircleFilled />
                                <span>
                                    Your connected EHR does not always tell us which provider saw a patient. When it
                                    does not, the visit is still billed, even for a provider you stop.
                                </span>
                            </div>
                        )}
                        <ProviderPicker
                            providers={state.providers}
                            value={provIds}
                            onChange={setProvIds}
                            selfProviderId={selfProviderId}
                            disabledIds={offEverywhereIds}
                        />
                    </div>
                )}
            </>
        );
    } else if (step === 'ehr') {
        body = (
            <>
                {intro}
                <div className={s.label}>{group ? 'In which EHRs' : 'Stop billing for'}</div>
                {group && (
                    <p className={s.who}>For {provScope === 'all' ? 'all providers' : 'the providers you selected'}</p>
                )}
                <div className={s.scope}>
                    <CardRadioGroup
                        value={ehrScope}
                        onChange={setEhrScope}
                        options={[
                            {
                                value: 'all',
                                title: 'All connected EHRs',
                                desc: 'Klarity stops creating and submitting claims for every EHR.',
                            },
                            // 一个 EHR 都没连时没得选，只留「All」
                            ...(allEhrNames.length
                                ? [
                                      {
                                          value: 'some',
                                          title: 'Selected EHRs only',
                                          desc: 'Klarity keeps billing the EHRs you do not select.',
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </div>
                {ehrScope === 'some' && (
                    <div className={s.pickWrap}>
                        <EhrTiles names={allEhrNames} value={ehrs} onChange={setEhrs} disabledNames={disabledEhrs} />
                    </div>
                )}
            </>
        );
    } else {
        body = (
            <>
                <div className={s.label}>Why are you turning it off?</div>
                <Radio.Group className={s.reasons} value={reason} onChange={(e) => setReason(e.target.value)}>
                    {CLAIM_OFF_REASONS.map((r) => (
                        <Radio key={r} value={r}>
                            {r}
                        </Radio>
                    ))}
                </Radio.Group>
                {reason === OTHERS_REASON && (
                    <Input.TextArea
                        className={s.note}
                        value={note}
                        maxLength={NOTE_MAX}
                        showCount
                        placeholder="Tell us more (optional)"
                        onChange={(e) => setNote(e.target.value)}
                    />
                )}
            </>
        );
    }

    return (
        <ConfirmModal
            open={open}
            title="Turn off managed billing"
            onClose={onClose}
            footer={
                <>
                    {at === 0 ? (
                        <Button onClick={onClose}>Cancel</Button>
                    ) : (
                        <Button onClick={() => setAt(at - 1)}>Back</Button>
                    )}
                    {last ? (
                        <Button
                            type="primary"
                            danger
                            className={s.dangerBtn}
                            disabled={!stepValid}
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            Turn off
                        </Button>
                    ) : (
                        <Button type="primary" disabled={!stepValid} onClick={() => setAt(at + 1)}>
                            Continue
                        </Button>
                    )}
                </>
            }
        >
            <div className={s.stepNo}>
                Step {at + 1} of {steps.length}
            </div>
            {body}
        </ConfirmModal>
    );
}
